import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import { 
  Users, 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  ChevronLeft, 
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Truck,
  ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  new_application: 'bg-blue-100 text-blue-800',
  reviewed: 'bg-purple-100 text-purple-800',
  approved_pending_docs: 'bg-yellow-100 text-yellow-800',
  docs_submitted: 'bg-orange-100 text-orange-800',
  docs_verified: 'bg-teal-100 text-teal-800',
  contract_sent: 'bg-indigo-100 text-indigo-800',
  contract_executed: 'bg-cyan-100 text-cyan-800',
  active_dispatch_eligible: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800'
};

const OnboardingQueue = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [applications, setApplications] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [fromDashboard, setFromDashboard] = useState(false);
  const limit = 20;

  const allowedRoles = ['admin', 'super_admin', 'dispatcher', 'safety_compliance_admin', 'ops_admin'];

  useEffect(() => {
    if (user && !allowedRoles.includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Read URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    
    if (statusParam) {
      setStatusFilter(statusParam);
      setFromDashboard(true);
    }
  }, [location.search]);

  const fetchApplications = async () => {
    setLoading(true);
    setError('');
    try {
      let url = `${API_URL}/api/onboarding/queue?limit=${limit}&skip=${page * limit}`;
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch applications');
      }
      
      const data = await response.json();
      setApplications(data.applications || []);
      setStatuses(data.statuses || {});
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchApplications();
    }
  }, [token, page, statusFilter]);

  const filteredApplications = applications.filter(app => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      `${app.first_name} ${app.last_name}`.toLowerCase().includes(search) ||
      app.email.toLowerCase().includes(search) ||
      app.phone.includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDriverType = (type) => {
    const types = {
      'lease_on': 'Lease-On',
      'owner_operator': 'Owner-Operator',
      'dispatch_only': 'Dispatch-Only',
      'company_driver': 'Company Driver'
    };
    return types[type] || type;
  };

  const formatLane = (lane) => {
    const lanes = {
      'otr': 'OTR',
      'regional': 'Regional',
      'dedicated': 'Dedicated',
      'any': 'Any'
    };
    return lanes[lane] || lane;
  };

  const formatExperience = (exp) => {
    const experiences = {
      'less_than_1': '< 1 year',
      '1_2': '1-2 years',
      '2_5': '2-5 years',
      '5_10': '5-10 years',
      '10_plus': '10+ years'
    };
    return experiences[exp] || exp;
  };

  if (!user || !allowedRoles.includes(user.role)) {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="onboarding-queue-page">
        {/* Breadcrumb Navigation */}
        <Breadcrumb 
          items={[
            { label: 'Onboarding' }
          ]}
        />

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-900" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Driver Onboarding Queue</h1>
              <p className="text-sm text-gray-500">
                {statusFilter && statuses[statusFilter] 
                  ? `Filtered: ${statuses[statusFilter]}` 
                  : 'Manage driver applications and onboarding workflow'}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex gap-3">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                  data-testid="status-filter"
                  className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                >
                  <option value="">All Statuses</option>
                  {Object.entries(statuses).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={fetchApplications}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
              Loading applications...
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No applications found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="applications-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Driver Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Preferred Lane</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Years Exp</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Last Updated</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredApplications.map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50" data-testid={`application-row-${app.id}`}>
                      <td className="px-4 py-3">
                        <Link to={`/admin/person/${app.id}`} data-testid={`person-file-link-${app.id}`}
                          className="font-medium text-blue-900 hover:underline">{app.first_name} {app.last_name}</Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{app.phone}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{app.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDriverType(app.driver_type)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatLane(app.preferred_lane)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatExperience(app.years_experience)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[app.status] || 'bg-gray-100 text-gray-800'}`}>
                          {app.status_label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(app.updated_at)}</td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/onboarding/${app.id}`}
                          data-testid={`view-details-${app.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-900 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} applications
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * limit >= total}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OnboardingQueue;
